/**
 * Bootstrap
 * Dependency injection setup for the application
 *
 * This is where we wire up the adapters based on the environment.
 * The UI components never directly import adapters - they get them
 * through the AppContext.
 *
 * API and storage settings are loaded from the SQLite database,
 * not from environment variables.
 */

import { detectEnvironment, Environment, getAPIBaseUrl } from './environment'

// Adapters
import { createElectronDatabaseAdapter, restDatabaseAdapter, createDynamoDBDatabaseAdapter } from './adapters/database'
import { mockFileStorageAdapter, createElectronFileStorageAdapter, createRestFileStorageAdapter } from './adapters/filestorage'
import { mockModernizeAPIAdapter, createModernizeAPIAdapter } from './adapters/api'

// Default configuration (used until settings are loaded from DB)
// Get API URL from environment (VITE_API_URL or defaults based on environment)
const DEFAULT_API_BASE_URL = getAPIBaseUrl()
const DEFAULT_USE_REAL_API = true

/**
 * Get user email from URL parameters (Portal mode)
 * If present, this indicates we're running as Portal with DynamoDB
 */
function getUserEmailFromURL() {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('user')
}

/**
 * Bootstrap the application with the appropriate adapters
 *
 * Database selection:
 * - Electron: SQLite via IPC to main process
 * - Web + URL param ?user=email: DynamoDB (Portal mode from ScoutITAI)
 * - Web (no user param): SQLite via REST API (local development)
 *
 * API settings (useRealApi, apiBaseUrl) are loaded from the database.
 *
 * @returns {Object} Container with all adapters and services
 */
export function bootstrap() {
  const env = detectEnvironment()
  const userEmail = getUserEmailFromURL()

  console.log(`[Bootstrap] Environment: ${env}`)
  if (userEmail) {
    console.log(`[Bootstrap] Portal mode detected - user: ${userEmail}`)
  }

  // Select adapters based on environment
  let databaseAdapter
  let fileStorageAdapter
  let modernizeAPIAdapter
  let aiProviderAdapter

  // Current settings (will be loaded from DB)
  let currentSettings = {
    apiBaseUrl: DEFAULT_API_BASE_URL,
    useRealApi: DEFAULT_USE_REAL_API,
  }

  if (env === Environment.ELECTRON) {
    // Electron mode - use SQLite via IPC and local filesystem
    console.log('[Bootstrap] Electron mode - SQLite via IPC')
    databaseAdapter = createElectronDatabaseAdapter()
    fileStorageAdapter = createElectronFileStorageAdapter()
  } else {
    // Web mode - ALWAYS use REST API (DynamoDB backend)
    console.log('[Bootstrap] Web mode - REST API (DynamoDB)')
    databaseAdapter = restDatabaseAdapter

    // REST file adapter to API
    console.log(`[Bootstrap] Using REST file storage adapter at ${DEFAULT_API_BASE_URL}`)
    fileStorageAdapter = createRestFileStorageAdapter({ baseUrl: DEFAULT_API_BASE_URL })
  }

  // AI provider (not yet implemented)
  aiProviderAdapter = null

  // ModernizeIT API - start with real API using defaults
  console.log(`[Bootstrap] Using real ModernizeIT API at ${DEFAULT_API_BASE_URL}`)
  modernizeAPIAdapter = createModernizeAPIAdapter({ baseUrl: DEFAULT_API_BASE_URL })

  // Create the container with all dependencies
  const container = {
    // Environment
    environment: env,

    // Adapters (ports implementations)
    adapters: {
      database: databaseAdapter,
      fileStorage: fileStorageAdapter,
      modernizeAPI: modernizeAPIAdapter,
      aiProvider: aiProviderAdapter,
    },

    // Convenience methods for common operations
    // These will be replaced with use cases later

    // Database operations
    db: {
      // Accounts
      getAccounts: () => databaseAdapter.getAccounts(),
      getAccount: (id) => databaseAdapter.getAccount(id),
      getDefaultAccount: () => databaseAdapter.getDefaultAccount(),
      createAccount: (data) => databaseAdapter.createAccount(data),
      updateAccount: (id, data) => databaseAdapter.updateAccount(id, data),
      deleteAccount: (id) => databaseAdapter.deleteAccount(id),

      // Projects
      getProjects: (accountId) => databaseAdapter.getProjects?.(accountId),
      getProject: (accountId, projectId) => databaseAdapter.getProject?.(accountId, projectId),
      createProject: (accountId, data) => databaseAdapter.createProject?.(accountId, data),
      updateProject: (accountId, projectId, data) => databaseAdapter.updateProject?.(accountId, projectId, data),
      deleteProject: (accountId, projectId) => databaseAdapter.deleteProject?.(accountId, projectId),

      // Applications
      getApplications: (accountId) => databaseAdapter.getApplications(accountId),
      getApplication: (id) => databaseAdapter.getApplication(id),
      createApplication: (data) => databaseAdapter.createApplication(data),
      updateApplication: (id, data) => databaseAdapter.updateApplication(id, data),
      deleteApplication: (id) => databaseAdapter.deleteApplication(id),

      // Jobs
      getJobs: (applicationId) => databaseAdapter.getJobs(applicationId),
      getJob: (id) => databaseAdapter.getJob(id),
      createJob: (data) => databaseAdapter.createJob(data),
      updateJob: (id, data) => databaseAdapter.updateJob(id, data),

      // Workflows
      getWorkflows: (applicationId) => databaseAdapter.getWorkflows(applicationId),
      getWorkflow: (id) => databaseAdapter.getWorkflow(id),
      createWorkflow: (data) => databaseAdapter.createWorkflow(data),
      updateWorkflow: (id, data) => databaseAdapter.updateWorkflow(id, data),
      deleteWorkflow: (id) => databaseAdapter.deleteWorkflow(id),

      // Settings
      getSettings: () => databaseAdapter.getSettings(),
      updateSettings: (data) => databaseAdapter.updateSettings(data),
      getAWSCredentials: () => databaseAdapter.getAWSCredentials(),
      updateAWSCredentials: (data) => databaseAdapter.updateAWSCredentials(data),
      getAIProvider: (provider) => databaseAdapter.getAIProvider(provider),
      updateAIProvider: (provider, data) => databaseAdapter.updateAIProvider(provider, data),

      // File Storage & API Settings
      getFileStorageSettings: () => databaseAdapter.getFileStorageSettings(),
      updateFileStorageSettings: (data) => databaseAdapter.updateFileStorageSettings(data),
    },

    // File storage operations
    files: {
      listFiles: (appId, path) => fileStorageAdapter.listFiles(appId, path),
      readFile: (appId, path) => fileStorageAdapter.readFile(appId, path),
      writeFile: (appId, path, content) => fileStorageAdapter.writeFile(appId, path, content),
      deleteFile: (appId, path) => fileStorageAdapter.deleteFile(appId, path),
      uploadFiles: (appId, files, dest) => fileStorageAdapter.uploadFiles(appId, files, dest),

      // Helper methods for UI
      getProjectFiles: (appId) => fileStorageAdapter.getProjectFiles(appId),
      getCobolFileWithSections: (appId, fileId) => fileStorageAdapter.getCobolFileWithSections(appId, fileId),
      getJavaSkeleton: (appId) => fileStorageAdapter.getJavaSkeleton(appId),
    },

    // ModernizeIT API operations
    api: {
      healthCheck: () => modernizeAPIAdapter.healthCheck(),

      // Ingest
      startIngest: (params) => modernizeAPIAdapter.startIngest(params),
      getIngestStatus: (jobId) => modernizeAPIAdapter.getIngestStatus(jobId),
      getIngestResults: (jobId) => modernizeAPIAdapter.getIngestResults(jobId),

      // Code Analysis
      startCodeAnalysis: (params) => modernizeAPIAdapter.startCodeAnalysis(params),
      getCodeAnalysisStatus: (jobId) => modernizeAPIAdapter.getCodeAnalysisStatus(jobId),
      getCodeAnalysisResults: (jobId) => modernizeAPIAdapter.getCodeAnalysisResults(jobId),

      // Code Refactor
      startCodeRefactor: (params) => modernizeAPIAdapter.startCodeRefactor(params),
      transformCode: (params) => modernizeAPIAdapter.transformCode(params),
      getCodeRefactorStatus: (jobId) => modernizeAPIAdapter.getCodeRefactorStatus(jobId),
      getCodeRefactorResults: (jobId) => modernizeAPIAdapter.getCodeRefactorResults(jobId),

      // Dependency Mapper
      startDependencyMapper: (params) => modernizeAPIAdapter.startDependencyMapper(params),
      getDependencyMapperStatus: (jobId) => modernizeAPIAdapter.getDependencyMapperStatus(jobId),
      getDependencyMapperResults: (jobId) => modernizeAPIAdapter.getDependencyMapperResults(jobId),

      // Monolith Identifier
      startMonolithIdentifier: (params) => modernizeAPIAdapter.startMonolithIdentifier(params),
      getMonolithIdentifierStatus: (jobId) => modernizeAPIAdapter.getMonolithIdentifierStatus(jobId),
      getMonolithIdentifierResults: (jobId) => modernizeAPIAdapter.getMonolithIdentifierResults(jobId),

      // Data Analysis
      startDataAnalysis: (params) => modernizeAPIAdapter.startDataAnalysis(params),
      getDataAnalysisStatus: (jobId) => modernizeAPIAdapter.getDataAnalysisStatus(jobId),
      getDataAnalysisResults: (jobId) => modernizeAPIAdapter.getDataAnalysisResults(jobId),

      // Discovery
      startDiscovery: (params) => modernizeAPIAdapter.startDiscovery(params),
      getDiscoveryResults: (jobId, section) => modernizeAPIAdapter.getDiscoveryResults(jobId, section),
      calculateROI: (params) => modernizeAPIAdapter.calculateROI(params),

      // Architecture
      startArchitecture: (params) => modernizeAPIAdapter.startArchitecture(params),
      getArchitectureStatus: (jobId) => modernizeAPIAdapter.getArchitectureStatus(jobId),
      getArchitectureResults: (jobId, section) => modernizeAPIAdapter.getArchitectureResults(jobId, section),

      // Generic job methods
      getJobStatus: (type, jobId) => modernizeAPIAdapter.getJobStatus(type, jobId),
      getJobResults: (type, jobId) => modernizeAPIAdapter.getJobResults(type, jobId),
    },

    // AI operations (placeholder)
    ai: {
      transform: async (params) => {
        console.warn('[Bootstrap] AI adapter not configured')
        return { transformedCode: '', explanation: 'AI not configured' }
      },
      explain: async (params) => {
        console.warn('[Bootstrap] AI adapter not configured')
        return { documentation: 'AI not configured' }
      },
      review: async (params) => {
        console.warn('[Bootstrap] AI adapter not configured')
        return { findings: [] }
      },
      chat: async (params) => {
        console.warn('[Bootstrap] AI adapter not configured')
        return { response: 'AI not configured' }
      },
    },

    // Settings management
    settings: {
      /**
       * Get current settings
       */
      getCurrent: () => ({ ...currentSettings }),

      /**
       * Load settings from database and reconfigure adapters
       * Should be called on app initialization
       */
      load: async () => {
        try {
          console.log('[Bootstrap] Loading settings from database...')
          const settings = await databaseAdapter.getFileStorageSettings()

          if (settings) {
            currentSettings.apiBaseUrl = settings.apiBaseUrl || DEFAULT_API_BASE_URL
            currentSettings.useRealApi = settings.useRealApi !== false

            console.log(`[Bootstrap] Settings loaded:`, currentSettings)

            // Reconfigure adapters with loaded settings
            if (env !== Environment.ELECTRON) {
              // Reconfigure REST file storage adapter
              fileStorageAdapter = createRestFileStorageAdapter({
                baseUrl: currentSettings.apiBaseUrl,
              })
              container.adapters.fileStorage = fileStorageAdapter

              // Update file operations to use new adapter
              container.files.listFiles = (appId, path) => fileStorageAdapter.listFiles(appId, path)
              container.files.readFile = (appId, path) => fileStorageAdapter.readFile(appId, path)
              container.files.writeFile = (appId, path, content) => fileStorageAdapter.writeFile(appId, path, content)
              container.files.deleteFile = (appId, path) => fileStorageAdapter.deleteFile(appId, path)
              container.files.uploadFiles = (appId, files, dest) => fileStorageAdapter.uploadFiles(appId, files, dest)
              container.files.getProjectFiles = (appId) => fileStorageAdapter.getProjectFiles(appId)
              container.files.getCobolFileWithSections = (appId, fileId) => fileStorageAdapter.getCobolFileWithSections(appId, fileId)
              container.files.getJavaSkeleton = (appId) => fileStorageAdapter.getJavaSkeleton(appId)
            }

            // Reconfigure ModernizeIT API adapter
            if (currentSettings.useRealApi) {
              modernizeAPIAdapter = createModernizeAPIAdapter({
                baseUrl: currentSettings.apiBaseUrl,
              })
              console.log(`[Bootstrap] Using real ModernizeIT API at ${currentSettings.apiBaseUrl}`)
            } else {
              modernizeAPIAdapter = mockModernizeAPIAdapter
              console.log('[Bootstrap] Using mock ModernizeIT API')
            }
            container.adapters.modernizeAPI = modernizeAPIAdapter

            // Update API operations to use new adapter
            container.api.healthCheck = () => modernizeAPIAdapter.healthCheck()
            container.api.startIngest = (params) => modernizeAPIAdapter.startIngest(params)
            container.api.getIngestStatus = (jobId) => modernizeAPIAdapter.getIngestStatus(jobId)
            container.api.getIngestResults = (jobId) => modernizeAPIAdapter.getIngestResults(jobId)
            container.api.startCodeAnalysis = (params) => modernizeAPIAdapter.startCodeAnalysis(params)
            container.api.getCodeAnalysisStatus = (jobId) => modernizeAPIAdapter.getCodeAnalysisStatus(jobId)
            container.api.getCodeAnalysisResults = (jobId) => modernizeAPIAdapter.getCodeAnalysisResults(jobId)
            container.api.startCodeRefactor = (params) => modernizeAPIAdapter.startCodeRefactor(params)
            container.api.transformCode = (params) => modernizeAPIAdapter.transformCode(params)
            container.api.getCodeRefactorStatus = (jobId) => modernizeAPIAdapter.getCodeRefactorStatus(jobId)
            container.api.getCodeRefactorResults = (jobId) => modernizeAPIAdapter.getCodeRefactorResults(jobId)
            container.api.startDependencyMapper = (params) => modernizeAPIAdapter.startDependencyMapper(params)
            container.api.getDependencyMapperStatus = (jobId) => modernizeAPIAdapter.getDependencyMapperStatus(jobId)
            container.api.getDependencyMapperResults = (jobId) => modernizeAPIAdapter.getDependencyMapperResults(jobId)
            container.api.startMonolithIdentifier = (params) => modernizeAPIAdapter.startMonolithIdentifier(params)
            container.api.getMonolithIdentifierStatus = (jobId) => modernizeAPIAdapter.getMonolithIdentifierStatus(jobId)
            container.api.getMonolithIdentifierResults = (jobId) => modernizeAPIAdapter.getMonolithIdentifierResults(jobId)
            container.api.startDataAnalysis = (params) => modernizeAPIAdapter.startDataAnalysis(params)
            container.api.getDataAnalysisStatus = (jobId) => modernizeAPIAdapter.getDataAnalysisStatus(jobId)
            container.api.getDataAnalysisResults = (jobId) => modernizeAPIAdapter.getDataAnalysisResults(jobId)
            container.api.startDiscovery = (params) => modernizeAPIAdapter.startDiscovery(params)
            container.api.getDiscoveryResults = (jobId, section) => modernizeAPIAdapter.getDiscoveryResults(jobId, section)
            container.api.calculateROI = (params) => modernizeAPIAdapter.calculateROI(params)
            container.api.startArchitecture = (params) => modernizeAPIAdapter.startArchitecture(params)
            container.api.getArchitectureStatus = (jobId) => modernizeAPIAdapter.getArchitectureStatus(jobId)
            container.api.getArchitectureResults = (jobId, section) => modernizeAPIAdapter.getArchitectureResults(jobId, section)
            container.api.getJobStatus = (type, jobId) => modernizeAPIAdapter.getJobStatus(type, jobId)
            container.api.getJobResults = (type, jobId) => modernizeAPIAdapter.getJobResults(type, jobId)
          }

          return currentSettings
        } catch (error) {
          console.error('[Bootstrap] Failed to load settings:', error)
          return currentSettings
        }
      },
    },
  }

  return container
}

// Create and export a singleton container
let _container = null

export function getContainer() {
  if (!_container) {
    _container = bootstrap()
  }
  return _container
}

// Reset container (useful for testing)
export function resetContainer() {
  _container = null
}
